import { z } from 'zod';

import { ExperienceAsResourceSchema, ExperienceSchema } from './common';
import { ResourceGroupResponseSchema } from './resource-group';
import { ExperienceStatus } from '../../experience/const';
import { makeEndpoint } from '../common';
import { ResourcePoolType } from '../resource-pool/resource-pool';

export type Experience = z.infer<typeof ExperienceSchema>;

export const bulkResourcesCommonFields = ExperienceAsResourceSchema.pick({
  customQuestions: true,
  priceTiers: true,
  priceTierRules: true,
  sessionDurationMinutes: true,
  isTicketed: true,
  cancellationPolicyId: true,
  reschedulePolicyId: true,
  limitedTimeId: true,
})
  .partial({ sessionDurationMinutes: true })
  .extend({
    bookingAvailabilityMode: ExperienceAsResourceSchema.shape.bookingAvailabilityMode.unwrap().optional(),
    displayedCapacity: ExperienceAsResourceSchema.shape.displayedCapacity.unwrap().optional(),
    description: ExperienceAsResourceSchema.shape.description.unwrap().optional(),
    isUnlisted: ExperienceAsResourceSchema.shape.isUnlisted.unwrap().optional(),
    onlineBookingCutoffHours: ExperienceAsResourceSchema.shape.onlineBookingCutoffHours.unwrap().optional().nullable(),
    emailMessage: ExperienceAsResourceSchema.shape.emailMessage.unwrap().optional(),
    notificationEmails: ExperienceAsResourceSchema.shape.notificationEmails.unwrap().optional(),
    notificationOverrides: ExperienceAsResourceSchema.shape.notificationOverrides.unwrap().optional(),
    paymentMethods: ExperienceAsResourceSchema.shape.paymentMethods.unwrap().optional(),
    shouldValidateComplimentaryBooking: ExperienceAsResourceSchema.shape.shouldValidateComplimentaryBooking
      .unwrap()
      .optional(),
    integrationMeta: ExperienceAsResourceSchema.shape.integrationMeta.unwrap().optional(),
    taxes: ExperienceAsResourceSchema.shape.taxes.unwrap().optional(),
    visibleOnCalendar: ExperienceAsResourceSchema.shape.visibleOnCalendar.unwrap().optional(),
  });

const bulkResourcesUniqueFields = ExperienceAsResourceSchema.extend({
  id: ExperienceAsResourceSchema.shape.id.optional(),
  title: ExperienceAsResourceSchema.shape.title.max(128, { message: 'Title must be at most 128 characters long.' }),
  maxParticipantsCount: ExperienceAsResourceSchema.shape.maxParticipantsCount.optional(),
}).pick({
  id: true,
  title: true,
  number: true,
  position: true,
  shape: true,
  maximumAllowedToBook: true,
  minimumRequiredToBook: true,
  maxParticipantsCount: true,
});

/**
 * Where a RESERVE group's inventory comes from. `create` makes a pool with one
 * unit per experience sent; `existing` sells every active unit of a pool, so
 * the server makes one experience per unit and `experiences` is ignored.
 */
export const BulkCreateResourcePoolSourceSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('create'),
    name: z.string().trim().min(1).max(255).optional(),
    type: z.nativeEnum(ResourcePoolType).optional(),
  }),
  z.object({ mode: z.literal('existing'), resourcePoolId: z.string().uuid() }),
]);
export type BulkCreateResourcePoolSource = z.infer<typeof BulkCreateResourcePoolSourceSchema>;

export const BulkCreateExperiencesRequestSchema = z.object({
  commonFields: bulkResourcesCommonFields,
  experiences: z.array(bulkResourcesUniqueFields),
  resourcePool: BulkCreateResourcePoolSourceSchema.optional(),
});
export const BulkCreateExperiencesEndpoint = makeEndpoint({
  method: 'POST',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    resourceGroupId: string;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}/experiences`,
  requestSchema: BulkCreateExperiencesRequestSchema.strict(),
  responseSchema: ResourceGroupResponseSchema,
});

export const BulkUpdateExperiencesRequestSchema = z.object({
  commonFields: bulkResourcesCommonFields.partial(),
  experiences: z.array(bulkResourcesUniqueFields.required({ id: true })),
});
export const BulkUpdateExperiencesEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({
    brandId,
    resourceGroupCollectionId,
    resourceGroupId,
  }: {
    brandId: string;
    resourceGroupCollectionId: string;
    resourceGroupId: string;
  }) =>
    `/v1/brands/${brandId}/resource-group-collections/${resourceGroupCollectionId}/resource-groups/${resourceGroupId}/experiences`,
  requestSchema: BulkUpdateExperiencesRequestSchema.strict(),
  responseSchema: ResourceGroupResponseSchema,
});

export const DeleteResourceEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId, resourceId, status }: { brandId: string; resourceId: string; status: ExperienceStatus }) =>
    `v1/brands/${brandId}/experiences/${resourceId}?status=${status}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});
